import axios from 'axios'
import { Fragment, useEffect, useState } from 'react'
import { useLinxUser } from 'src/sdk/linx/hooks/useLinxUser'
import { useLocation } from '@reach/router'
import { useStoreConfig } from 'src/hooks/useStoreConfig'

import Section from '../sections/Section'
import ProductShelfSkeleton from '../skeletons/ProductShelfSkeleton'
import { ShelfBoughtTogether } from './ShelfBoughtTogether/ShelfBoughtTogether'
import { ShelfGeneric } from './ShelfGeneric'
import type { Product } from './ShelfBoughtTogether/types'

type DataRecommendation = {
  position: string
  showcases: Showcases[]
  id: string
  feature: string
}

type Showcases = {
  context: string
  display: Display[]
  feature: string
  id: string
  impressionUrl: string
  name: string
  subtitle: string
  title: string
}

type Display = {
  recomendations: Product[]
  references: Product[]
}

type LinxProps = {
  position: string
  page: string
  productId?: string
  classNameContainer?: string
  breakpoints?: {
    [key: number]: {
      slidesPerView: number
    }
  }
}

type RecommendationsParams = {
  deviceId: string
  productId?: string
  userId?: string
  salesChannel?: number
}

export default function ShelfLinx({
  position,
  page,
  productId,
  breakpoints,
  classNameContainer,
}: LinxProps) {
  const {
    search: {
      recommendation: { salesChannel },
    },
  } = useStoreConfig()

  const { getLinxUser } = useLinxUser()
  const location = useLocation()
  const [loading, setLoading] = useState(false)
  const [loadingBoughtTogether, setLoadingBoughtTogether] = useState(true)
  const [recommendations, setRecommendations] = useState<DataRecommendation[]>(
    []
  )

  const [currentProductBoughtTogether, setCurrentProductBoughtTogether] =
    useState<Product | null>(null)

  const [allProductsBoughtTogether, setAllProductsBoughtTogether] = useState<
    Product[]
  >([])

  const [error, setError] = useState(false)

  useEffect(() => {
    const linxUser = getLinxUser()

    const getRecommendations = async () => {
      try {
        setLoading(true)

        let params: RecommendationsParams = {
          productId,
          deviceId: linxUser.deviceId,
          salesChannel,
        }

        if (linxUser.loggedIn) {
          params = { ...params, userId: (linxUser as { id: string }).id }
        }

        const { data: response } = await axios.get(
          `https://decathlonstore.myvtex.com/_v2/api/decathlon-search/pages/recommendations?source=desktop&name=${page}`,
          {
            params,
          }
        )

        const dataShelf = response.filter(
          (item: DataRecommendation) => item.position === position
        )

        for (const item of dataShelf) {
          for (const showcase of item.showcases) {
            if (showcase.title === 'Compre Junto') {
              setCurrentProductBoughtTogether(
                showcase.displays[0].references[0]
              )
              setAllProductsBoughtTogether(showcase.displays[0].recommendations)
            }
          }
        }

        setLoading(false)
        setRecommendations(
          dataShelf[0].showcases.length ? dataShelf[0].showcases : []
        )
      } catch (e) {
        console.error(e)
        setLoading(false)
        setError(true)
      }
    }

    getRecommendations()
  }, [getLinxUser, page, position, productId, salesChannel])

  useEffect(() => {
    if (!allProductsBoughtTogether) {
      return
    }

    setLoadingBoughtTogether(false)
  }, [allProductsBoughtTogether])

  const { tenant } = useStoreConfig()

  if (tenant === 'B2B') {
    return <></>
  }

  return (
    <>
      {!error && (
        <>
          <ProductShelfSkeleton loading={!recommendations.length && loading}>
            {recommendations?.map((item) => (
              <Fragment key={item.id}>
                {item.feature !== 'FrequentlyBoughtTogether' && (
                  <Section
                    className={`layout__content layout__section shelf-section ${classNameContainer}`}
                  >
                    <ShelfGeneric item={item} breakpoints={breakpoints} />
                  </Section>
                )}
              </Fragment>
            ))}
          </ProductShelfSkeleton>

          {Boolean(location.pathname.match('/p')) &&
            currentProductBoughtTogether !== null && (
              <ProductShelfSkeleton
                loading={!recommendations.length && loadingBoughtTogether}
              >
                <Fragment>
                  <Section className="layout__content layout__section shelf-section">
                    <ShelfBoughtTogether
                      allItems={allProductsBoughtTogether}
                      currentItem={currentProductBoughtTogether}
                    />
                  </Section>
                </Fragment>
              </ProductShelfSkeleton>
            )}
        </>
      )}
    </>
  )
}
